import { FC, useEffect } from "react";
import { Box, VBox } from "../../components-v2/Box";
import {
  formatValidSubscribersLabel,
  IncreaseOfShareCapitalForm,
  QuickSetupType,
  quickSetupTypeLabels,
  quickSetupTypeTooltip,
} from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import {
  ArticleSelectorQuestion,
  DateQuestion,
  FromToPeriod,
  NumberQuestion,
  YesNoQuestion,
} from "../common/commonComponents";
import ContributionInKind from "./ContributionInKind";
import DebtContribution from "./DebtContribution";
import ShareCapitalIncreaseMethod from "./ShareCapitalIncreaseMethod";
import ShareIssueOrIncreaseNominalValue from "./ShareIssueOrIncreaseNominalValue";
import ShowPreferentialRight from "./ShowPreferentialRight";
import { IncreaseNominalValueShareContribution } from "./IncreaseNominalValue";
import SelectShareTypesForIssue from "./SelectShareTypesForIssue";
import ShowPowerOfAttorney from "./ShowPowerOfAttorney";
import { SpecialCircumstances } from "./SpecialCircumstances";
import { GuaranteeConsortium } from "./GuaranteeConsortium";
import { ReceiveShareholderRights } from "./ReceiveShareholderRights";
import { IsDisposableAccount } from "./IsDisposableAccount";
import { IsAccountGiven } from "./IsAccountGiven";
import { Warning } from "./Warning";
import { Alert } from "../../components-v2/Alert";
import { IncreaseNominalValue } from "../common/commonCapitalIncreaseComponents";
import { CURRENCY_NOK } from "../../utils/currency";
import { SubscribersStatus } from "./SubscribersStatus";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";

import { useTranslation } from "../../stores/RootStoreContext";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { ChangeArticlesOfAssociation } from "../changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { AddNameAndEmailForm } from "../../common/commonComponents";
import { RadioFieldModel } from "../../common/commonForms";
import { InfoButton } from "../../components-v2/InfoButton";
import { Markdown } from "../../text/Markdown";
import { Icon } from "../../components-v2/Icon";
import { QuickSetupDialog } from "./IncreaseOfShareCapitalQuickSetup";

const Subscribers: FC<{ form: IncreaseOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      {form.isPreferentialRight && (
        <Alert
          type={"info"}
          message={t("decisions.increaseShareCapitalAgainstCash.preferentialRightSubscriberDesc")}
        />
      )}
      <div className={"text-lg"}>
        <b>{t("subscribers.title")}</b>
      </div>
      <SubscribersStatus form={form} />
      {!form.isActiveSubscribersValid && <Alert type="info" message={t("subscribers.invalid")} />}
      {form.isActiveSubscribersValid && (
        <Alert
          type="success"
          message={t(formatValidSubscribersLabel(form.activeSubscribersForIssue))}
        />
      )}
    </VBox>
  );
});

const QuickSetupWizard: FC<{ form: IncreaseOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return !form.isQuickSetupApplied ? (
    <VBox paddingY={"lg"}>
      <div>{t("decisions.increaseShareCapital.quickSetupSelector.title")}</div>
      <Box wrap>
        {Object.values(QuickSetupType).map((type) => (
          <Button
            key={type}
            variant={"basic"}
            size={"sm"}
            icon={"plus"}
            label={quickSetupTypeLabels[type]}
            tooltip={quickSetupTypeTooltip[type]}
            onClick={() => form.setQuickSetup(type)}
            disabled={form.isProjectLocked}
          />
        ))}
      </Box>
    </VBox>
  ) : (
    <VBox gap={"lg"} paddingY={"lg"}>
      <VBox>
        <div>{t("decisions.increaseShareCapital.quickSetup.setup")}</div>
        <Box
          bg
          rounded
          border
          alignItems={"center"}
          paddingLeft={"md"}
          paddingRight={"sm"}
          paddingY={"sm"}
        >
          <Icon name={"magic-wand"} className={"subtle"} />
          <span>{t(quickSetupTypeLabels[form.quickSetupType!])}</span>
          <Box marginLeft={"auto"}>
            <IconButton
              icon={"x"}
              tooltip={t("remove")}
              color="gray"
              onClick={() => form.setQuickSetup(undefined)}
            />
          </Box>
        </Box>
        {form.isQuickSetupValid && (
          <Button
            variant={"secondary"}
            label={t("decisions.increaseShareCapital.quickSetup.edit")}
            onClick={form.openQuickSetupDialog}
            disabled={!form.isQuickSetupValid}
          />
        )}
      </VBox>
      <Alert
        message={
          form.isQuickSetupValid
            ? t("decisions.increaseShareCapital.quickSetupSelector.info")
            : t("decisions.increaseShareCapital.quickSetupSelector.info.incompatibleSetup")
        }
        type={form.isQuickSetupValid ? "info" : "warning"}
      />
      {form.quickSetupModel && <QuickSetupDialog form={form} model={form.quickSetupModel} />}
    </VBox>
  );
});

const IncreaseOfShareCapitalFormView: FC<{ form: IncreaseOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();

    return (
      <VBox gap={"md"}>
        <QuickSetupWizard form={form} />
        <YesNoQuestion
          question={t("decisions.increaseShareCapitalAgainstCash.usePowerOfAttorney")}
          field={form.usePowerOfAttorney}
          onChange={(e) => form.setUsePowerOfAttorney(e)}
          isLocked={form.isProjectLocked}
        />
        {form.showPowerOfAttorney && <ShowPowerOfAttorney form={form} />}

        {form.showShareIssueOrIncreaseNominalValue && (
          <>
            <ShareCapitalIncreaseMethod form={form} />
            {form.hasChosenIncreaseMethod && <ShareIssueOrIncreaseNominalValue form={form} />}
            {form.showShareIssue && (
              <>
                {form.showShareIssueNominalValue && (
                  <NumberQuestion
                    question={t("transactions.issueTransaction.nominal-amount")}
                    prefix={CURRENCY_NOK}
                    field={form.shareIssue.nominalValue}
                    isLocked={form.isProjectLocked}
                  />
                )}
                <SelectShareTypesForIssue model={form.shareIssue} />
                {form.showPreferentialRights && <ShowPreferentialRight form={form} />}
                {form.showSubscribers && <Subscribers form={form} />}
                {form.showSubscribersStatus && <SubscribersStatus form={form} />}
              </>
            )}
            {form.showIncreaseNominalValue && (
              <>
                <IncreaseNominalValue
                  increaseInNominalValue={form.increaseNominalValue.increaseInNominalValue}
                  currentNominalValue={form.currentNominalValue}
                  currentNumberOfShares={form.currentNumberOfShares}
                  isProjectLocked={form.isProjectLocked}
                />
                {!form.isNewNominalValueValid && (
                  <Warning
                    message={t(
                      "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newNominal-helperText",
                    )}
                  />
                )}
                {form.isNewNominalValueValid && !form.isNewShareCapitalValid && (
                  <Warning
                    message={t(
                      "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newShareCapital-helperText",
                    )}
                  />
                )}
                {form.showIncreaseContributionPerShare && (
                  <>
                    <IncreaseNominalValueShareContribution form={form} />
                    <SubscribersStatus form={form} />
                  </>
                )}
              </>
            )}

            {form.showDebtConversion && <DebtContribution form={form} />}
            {form.showContributionInKind && <ContributionInKind form={form} />}

            {form.showSubscriptionForm && (
              <>
                <YesNoQuestion
                  question={
                    form.showShareIssue
                      ? t("decisions.increaseShareCapitalAgainstCash.newShares.subscriptionForm")
                      : t(
                          "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.subscriptionForm",
                        )
                  }
                  infoText={t(
                    "decisions.increaseShareCapitalAgainstCash.subscriptionForm-infoText",
                  )}
                  field={form.isSubscriptionForm}
                  onChange={(e) => form.setIsSubscriptionForm(e)}
                  isLocked={form.isProjectLocked}
                />
                {form.isSubscriptionForm.value && (
                  <FromToPeriod
                    label={t("decisions.increaseShareCapitalAgainstCash.subscriptionFormPeriod")}
                    from={form.periodForSubscription.from}
                    to={form.periodForSubscription.to}
                    isLocked={form.isProjectLocked}
                  />
                )}
                {form.showIsSubscribersSigning && (
                  <>
                    <YesNoQuestion
                      question={t(
                        "decisions.increaseShareCapitalAgainstCash.isOtherSigningForSubscribers",
                      )}
                      field={form.isOtherSigningForAllSubscribers}
                      isLocked={form.isProjectLocked}
                    />
                    {form.showSignersForAllSubscribers && <SignersForAllSubscribers form={form} />}
                  </>
                )}
                {form.showDeadlineForSubscription && (
                  <>
                    <DateQuestion
                      question={t("decisions.increaseShareCapitalAgainstCash.payment.deadline")}
                      label={t("decisions.enter-date")}
                      field={form.deadlineForSubscription}
                      isLocked={form.isProjectLocked}
                    />
                    {form.deadlineForSubscriptionNotEmpty && (
                      <>
                        {form.showAccountInformation && (
                          <>
                            <IsDisposableAccount
                              isDisposableAccount={form.isDisposableAccount}
                              isProjectLocked={form.isProjectLocked}
                            />
                            <IsAccountGiven
                              isAccountGiven={form.isAccountGiven}
                              isProjectLocked={form.isProjectLocked}
                              accountNumber={form.accountNumber}
                            />
                          </>
                        )}
                        {form.showShareholderRights && (
                          <ReceiveShareholderRights
                            shareholderRights={form.shareIssue.shareholderRights}
                            isProjectLocked={form.isProjectLocked}
                          />
                        )}
                        <div data-testid={"estimatedCosts"}>
                          <NumberQuestion
                            field={form.estimatedCosts}
                            question={t("decisions.increaseShareCapitalAgainstCash.estimatedCosts")}
                            label={t("decisions.enter-amount")}
                            prefix={CURRENCY_NOK}
                            isLocked={form.isProjectLocked}
                          />
                        </div>
                        {form.showGuaranteeConsortium && <GuaranteeConsortium form={form} />}

                        <SpecialCircumstances
                          model={form.specialCircumstancesUponSubscriptionModel}
                          specialCircumstancesTexts={form.specialCircumstancesUponSubscriptionTexts}
                          question={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstances.questionText",
                          )}
                          addedExplanationQuestion={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesDescription",
                          )}
                          isProjectLocked={form.isProjectLocked}
                        />
                        <SpecialCircumstances
                          model={form.specialCircumstancesAfterLastApprovedAnnualAccountModel}
                          specialCircumstancesTexts={
                            form.specialCircumstancesAfterLastApprovedAnnualAccountTexts
                          }
                          question={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccount.questionText",
                          )}
                          addedExplanationQuestion={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccountDescription",
                          )}
                          isProjectLocked={form.isProjectLocked}
                        />
                        <UpdateArticlesAutomatically
                          updateArticlesAutomatically={form.updateArticlesAutomatically}
                          isProjectLocked={form.isProjectLocked}
                        />
                        {form.isUpdateArticlesAutomatically && (
                          <ArticleSelectorQuestion
                            question={t(
                              "decisions.increaseShareCapitalAgainstCash.which-paragraph",
                            )}
                            model={form.articleOfAssociationNumber}
                            isLocked={form.isProjectLocked}
                            onChange={form.articleOfAssociationNumber.setSelected}
                          />
                        )}
                        {form.isUpdateArticlesManually && (
                          <ChangeArticlesOfAssociation model={form.articleChangeModel} />
                        )}
                        {form.isCapitalCallEnabled && (
                          <VBox>
                            <div>
                              {t(
                                "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.title",
                              )}
                            </div>
                            <MRadioButtonGroup
                              name={"updateCommitmentAutomatically"}
                              model={form.updateCommitmentAutomatically}
                              disabled={form.isProjectLocked}
                            />
                          </VBox>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </VBox>
    );
  },
);

export const UpdateArticlesAutomatically = observer(function ({
  updateArticlesAutomatically,
  isProjectLocked,
}: {
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box>
        <div>
          {t("decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.title")}
        </div>
        <InfoButton
          icon={"info"}
          infoText={
            <Markdown
              value={t(
                "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.info",
              )}
            />
          }
        />
      </Box>
      <MRadioButtonGroup
        name={"updateArticlesAutomatically"}
        model={updateArticlesAutomatically}
        disabled={isProjectLocked}
      />
    </VBox>
  );
});

export const SignersForAllSubscribers: FC<{
  form: IncreaseOfShareCapitalForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      <div>
        {t("decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.questionText")}
      </div>
      {form.signersForAllSubscribers.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            tooltip={t("remove")}
            onClick={() => form.removeSignerForAllSubscribers(p)}
            disabled={form.isProjectLocked}
            compact
          />
        </Box>
      ))}
      <Box>
        {form.addSignerForAllSubscribersModel ? (
          <AddNameAndEmailForm
            form={form.addSignerForAllSubscribersModel}
            submit={form.submitSignerForAllSubscribers}
            cancel={form.cancelSignerForAllSubscribers}
          />
        ) : (
          <LinkButton
            icon={"plus"}
            label={t(
              "decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.addSigner",
            )}
            onClick={() => form.addSignerForAllSubscribers()}
            disabled={form.isProjectLocked}
          />
        )}
      </Box>
      {form.isSignersForAllSubscribersEmpty && (
        <Alert
          message={t("decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.info")}
          type={"info"}
        />
      )}
    </>
  );
});

export const IncreaseOfShareCapitalView: FC<{
  form: IncreaseOfShareCapitalForm;
  showOpenDetailsButton: boolean;
  openDetails: () => void;
}> = observer(({ form, showOpenDetailsButton, openDetails }) => {
  const { t } = useTranslation();
  useEffect(() => {
    return form.init();
  }, [form]);

  return showOpenDetailsButton ? (
    <VBox gap={"md"}>
      <Button
        variant={"secondary"}
        label={t("decisions.increaseShareCapitalAgainstCash.edit-settings")}
        onClick={openDetails}
      />
    </VBox>
  ) : (
    <IncreaseOfShareCapitalFormView form={form} />
  );
});
